import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Send as SendIcon
} from '@material-ui/icons'
import moment from 'moment'
import { useContext, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { AuthContext } from '../../context/Auth/AuthContext'
import { SocketContext } from '../../context/Socket/SocketContext'
import toastError from '../../errors/toastError'
import { taskCommentApi } from '../../services/taskComments'
import ConfirmationModal from '../ConfirmationModal'

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    height: '80vh',
    maxHeight: '80vh'
  },
  dialogContent: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0
  },
  commentsContainer: {
    flex: 1,
    overflowY: 'auto',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default
  },
  messageContainer: {
    display: 'flex',
    marginBottom: theme.spacing(1.5),
    '&.own-message': {
      justifyContent: 'flex-end'
    },
    '&.other-message': {
      justifyContent: 'flex-start'
    }
  },
  commentItem: {
    maxWidth: '70%',
    padding: theme.spacing(1.5),
    borderRadius: 18,
    position: 'relative',
    wordBreak: 'break-word',
    '&.own-message': {
      backgroundColor: '#6A5ACD',
      color: '#000000',
      marginLeft: 'auto',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        right: -8,
        width: 0,
        height: 0,
        border: '8px solid transparent',
        borderLeftColor: '#6A5ACD',
        borderTop: 'none',
        borderRight: 'none'
      }
    },
    '&.other-message': {
      backgroundColor: theme.palette.grey[200],
      color: '#000000',
      marginRight: 'auto',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: -8,
        width: 0,
        height: 0,
        border: '8px solid transparent',
        borderRightColor: theme.palette.grey[200],
        borderTop: 'none',
        borderLeft: 'none'
      }
    }
  },
  commentHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5)
  },
  commentAuthor: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: '0.75rem',
    opacity: 0.8
  },
  commentActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5)
  },
  commentDate: {
    fontSize: '0.7rem',
    opacity: 0.7,
    marginTop: theme.spacing(0.5),
    textAlign: 'right'
  },
  commentText: {
    lineHeight: 1.4,
    fontSize: '0.9rem',
    color: '#000000 !important'
  },
  inputContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-end',
    marginTop: 'auto'
  },
  inputField: {
    flex: 1
  },
  emptyState: {
    textAlign: 'center',
    color: theme.palette.text.secondary,
    padding: theme.spacing(4),
    fontStyle: 'italic'
  },
  editingField: {
    marginTop: theme.spacing(1),
    width: '100%'
  },
  messageInfo: {
    fontSize: '0.7rem',
    opacity: 0.7,
    marginTop: theme.spacing(0.5),
    '&.own-message': {
      textAlign: 'right',
      color: '#333333'
    },
    '&.other-message': {
      textAlign: 'left',
      color: '#333333'
    }
  }
}))

const TaskCommentsModal = ({ open, onClose, task }) => {
  const classes = useStyles()
  const { user } = useContext(AuthContext)
  const socketManager = useContext(SocketContext)

  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [sendingComment, setSendingComment] = useState(false)

  // Estados para edição
  const [editingComment, setEditingComment] = useState(null)
  const [editText, setEditText] = useState('')

  // Estados para confirmação de exclusão
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [commentToDelete, setCommentToDelete] = useState(null)

  const commentsEndRef = useRef(null)

  const scrollToBottom = () => {
    commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const fetchComments = async () => {
    if (!task?.id) return

    setLoading(true)
    try {
      const { data } = await taskCommentApi.list(task.id)
      setComments(data.taskComments || [])
    } catch (err) {
      toastError(err)
    }
    setLoading(false)
  }

  useEffect(() => {
    if (open && task?.id) {
      fetchComments()
    }
  }, [open, task?.id])

  useEffect(() => {
    scrollToBottom()
  }, [comments])

  // Socket para atualizações em tempo real
  useEffect(() => {
    if (!user?.companyId || !open) return

    const socket = socketManager.GetSocket(user.companyId)

    const onTaskComment = data => {
      if (data.action === 'create') {
        // Verifica se o comentário é da tarefa atual
        if (data.taskComment.taskId === task?.id) {
          setComments(prev => [...prev, data.taskComment])
        }
      } else if (data.action === 'update') {
        if (data.taskComment.taskId === task?.id) {
          setComments(prev =>
            prev.map(comment =>
              comment.id === data.taskComment.id ? data.taskComment : comment
            )
          )
        }
      } else if (data.action === 'delete') {
        setComments(prev =>
          prev.filter(comment => comment.id !== +data.taskCommentId)
        )
      }
    }

    socket.on('taskComment', onTaskComment)

    return () => {
      socket.off('taskComment', onTaskComment)
    }
  }, [user?.companyId, socketManager, open, task?.id])

  const handleSendComment = async () => {
    if (!newComment.trim() || !task?.id) return

    setSendingComment(true)
    try {
      await taskCommentApi.create({
        taskId: task.id,
        comment: newComment.trim()
      })
      setNewComment('')
      toast.success('Comentário adicionado com sucesso!')
    } catch (err) {
      toastError(err)
    }
    setSendingComment(false)
  }

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendComment()
    }
  }

  const handleEditComment = comment => {
    setEditingComment(comment.id)
    setEditText(comment.comment)
  }

  const handleSaveEdit = async () => {
    if (!editText.trim()) return

    try {
      await taskCommentApi.update(editingComment, {
        comment: editText.trim()
      })
      setEditingComment(null)
      setEditText('')
      toast.success('Comentário atualizado com sucesso!')
    } catch (err) {
      toastError(err)
    }
  }

  const handleCancelEdit = () => {
    setEditingComment(null)
    setEditText('')
  }

  const handleDeleteComment = async () => {
    if (!commentToDelete) return

    try {
      await taskCommentApi.delete(commentToDelete.id)
      setCommentToDelete(null)
      setConfirmModalOpen(false)
      toast.success('Comentário excluído com sucesso!')
    } catch (err) {
      toastError(err)
    }
  }

  const formatDate = date => {
    return moment(date).format('DD/MM/YYYY HH:mm')
  }

  // const getInitials = (name) => {
  //   return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  // };

  const canEditComment = comment => {
    return comment.userId === user?.id
  }

  const isOwnMessage = comment => {
    return comment.userId === user?.id
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            💬 Comentários da Tarefa: {task?.title || ''}
          </Box>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <div className={classes.commentsContainer}>
            {loading ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : comments.length === 0 ? (
              <div className={classes.emptyState}>
                Nenhum comentário ainda. Seja o primeiro a comentar!
              </div>
            ) : (
              <>
                {comments.map(comment => {
                  const isOwn = isOwnMessage(comment)
                  return (
                    <div
                      key={comment.id}
                      className={`${classes.messageContainer} ${isOwn ? 'own-message' : 'other-message'}`}
                    >
                      <Paper
                        className={`${classes.commentItem} ${isOwn ? 'own-message' : 'other-message'}`}
                        elevation={1}
                      >
                        {editingComment === comment.id ? (
                          <>
                            <TextField
                              multiline
                              rows={3}
                              value={editText}
                              onChange={e => setEditText(e.target.value)}
                              className={classes.editingField}
                              variant="outlined"
                              size="small"
                            />
                            <div className={classes.commentActions}>
                              <IconButton
                                size="small"
                                onClick={handleSaveEdit}
                                style={{ color: '#000000' }}
                              >
                                <SaveIcon />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={handleCancelEdit}
                                style={{ color: '#000000' }}
                              >
                                <CancelIcon />
                              </IconButton>
                            </div>
                          </>
                        ) : (
                          <>
                            <Typography
                              variant="caption"
                              style={{
                                fontWeight: 'bold',
                                fontSize: '0.75rem',
                                color: '#000000',
                                marginBottom: '4px',
                                display: 'block'
                              }}
                            >
                              {comment.user?.name || 'Usuário'}
                            </Typography>

                            <Typography className={classes.commentText}>
                              {comment.comment}
                            </Typography>

                            <div
                              className={`${classes.messageInfo} ${isOwn ? 'own-message' : 'other-message'}`}
                            >
                              {formatDate(comment.createdAt)}
                            </div>

                            {canEditComment(comment) && (
                              <div className={classes.commentActions}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditComment(comment)}
                                  style={{ color: '#000000' }}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setCommentToDelete(comment)
                                    setConfirmModalOpen(true)
                                  }}
                                  style={{ color: '#000000' }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </div>
                            )}
                          </>
                        )}
                      </Paper>
                    </div>
                  )
                })}
                <div ref={commentsEndRef} />
              </>
            )}
          </div>

          <Divider />

          <div className={classes.inputContainer}>
            <TextField
              multiline
              rows={2}
              placeholder="Digite seu comentário..."
              value={newComment}
              onChange={e => setNewComment(e.target.value)}
              onKeyPress={handleKeyPress}
              className={classes.inputField}
              variant="outlined"
              size="small"
              disabled={sendingComment}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendComment}
              disabled={!newComment.trim() || sendingComment}
              startIcon={
                sendingComment ? <CircularProgress size={16} /> : <SendIcon />
              }
            >
              {sendingComment ? 'Enviando...' : 'Enviar'}
            </Button>
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="primary">
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        title="Excluir Comentário"
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={handleDeleteComment}
      >
        Tem certeza que deseja excluir este comentário? Esta ação não pode ser
        desfeita.
      </ConfirmationModal>
    </>
  )
}

export default TaskCommentsModal
